
import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from "@/components/ui/card";
import { HelpCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Button } from '@/components/ui/button';
import { usePlaylistClips } from '@/hooks/usePlaylistClips';
import { useClipsFormatter } from '@/hooks/useClipsFormatter';
import ContentCarousel from '@/components/home/ContentCarousel';
import ClipCard from '@/components/home/ClipCard';
import { useIsMobile } from '@/hooks/use-mobile';

export const PressConceptSection = () => {
  const { language } = useLanguage();
  const lamourCestSurcotePlaylistId = "6eca71d9-371e-4223-8e96-478d9a38a0a8";
  const { clips, isLoading, error } = usePlaylistClips(lamourCestSurcotePlaylistId);
  const { formatClipsCallback } = useClipsFormatter();
  
  // Format clips for the carousel ClipCard component with proper type checking
  const formattedClips = React.useMemo(() => {
    if (!clips || !Array.isArray(clips) || clips.length === 0) {
      console.log("No clips available to format");
      return [];
    }
    
    const formatted = formatClipsCallback(clips);
    
    // Ensure formatted is an array
    if (!Array.isArray(formatted)) {
      console.log("Formatter did not return an array", formatted);
      return [];
    }
    
    return formatted
      .filter(clip => clip && typeof clip === 'object') // Filter out null/undefined/non-object items
      .map(clip => {
        // Provide default values for all required properties
        return {
          id: clip.id || `default-${Math.random()}`,
          title: clip.title || (language === 'fr' ? 'Sans titre' : 'Untitled'),
          thumbnailUrl: clip.thumbnailUrl || '/placeholder.svg',
          playlistName: clip.playlistName || (
            language === 'fr' ? "L'Amour c'est surcoté" : "Love is overrated"
          ),
          duration: typeof clip.duration === 'string' 
            ? clip.duration 
            : formatDuration(typeof clip.duration === 'number' ? clip.duration : 0)
        };
      });
  }, [clips, formatClipsCallback, language]);
  
  // Format duration as string for display
  function formatDuration(seconds) {
    if (!seconds || typeof seconds !== 'number') return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  
  return (
    <section id="concept-section" className="py-12 px-4 bg-muted/30">
      <div className="container mx-auto max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">
          🎬 {language === 'fr' ? 'Un nouveau terrain de jeu pour les créateurs' : 'A new playground for creators'}
        </h2>
        
        <div className="prose prose-lg dark:prose-invert max-w-none mb-10">
          <p>
            {language === 'fr' ? (
              <>
                Recut ouvre une nouvelle voie : permettre à une génération de créateurs de <strong>remixer des extraits de films dans un cadre légal</strong>, en partenariat avec les ayants droit.
              </>
            ) : (
              <>
                Recut opens a new path: enabling a generation of creators to <strong>remix film clips within a legal framework</strong>, in partnership with rights holders.
              </>
            )}
          </p>
          <p>
            {language === 'fr' ? (
              "Ce premier challenge a été mené avec StudioCanal autour du film L'Amour c'est surcoté : les extraits ci-dessous ont été mis à disposition des participants via une licence encadrée, pour donner naissance à leurs propres formats courts."
            ) : (
              "This first challenge was conducted with StudioCanal around the film 'L'Amour c'est surcoté': the clips below were made available to participants through a structured license, to give birth to their own short formats."
            )}
          </p>
          <p>
            {language === 'fr' ? (
              "Les créations issues de ce challenge prennent des formes diverses : remix, fiction, détournement, réaction…"
            ) : (
              "The creations from this challenge take various forms: remixes, fiction, repurposing, reactions..."
            )}
          </p>
          <p>
            {language === 'fr' ? (
              <>
                Loin d'une logique publicitaire ou critique classique, il s'agit ici d'<strong>activer une circulation créative, incarnée, spontanée</strong> — compatible avec le droit d'auteur.
              </>
            ) : (
              <>
                Far from a traditional advertising or critical approach, this is about <strong>activating creative, embodied, spontaneous circulation</strong> — compatible with copyright.
              </>
            )}
          </p>
          <p>
            {language === 'fr' ? (
              "Ces contenus ne sont pas librement exploitables dans un cadre professionnel. Le visionnage est ouvert, mais le téléchargement est réservé aux créateurs du challenge."
            ) : (
              "These contents are not freely usable in a professional setting. Viewing is open, but downloading is reserved for creators of the challenge."
            )}
          </p>
        </div>
        
        {/* Display original film clips */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              {language === 'fr' ? 'Extraits originaux du film' : 'Original film clips'}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help inline-flex">
                      <Info className="h-5 w-5 text-primary/80" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs p-3 bg-popover text-popover-foreground">
                    <p>
                      ❗ {language === 'fr' 
                        ? "Ces contenus ne sont pas librement exploitables dans un cadre professionnel. Le visionnage est ouvert, mais le téléchargement est réservé aux créateurs du challenge."
                        : "These contents are not freely usable in a professional setting. Viewing is open, but downloading is reserved for creators of the challenge."}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h3>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-muted animate-pulse h-48 rounded-lg"></div>
              ))}
            </div>
          ) : error ? (
            <p className="text-destructive">
              {language === 'fr' ? 'Erreur lors du chargement des clips' : 'Error loading clips'}
            </p>
          ) : formattedClips && formattedClips.length > 0 ? (
            <ContentCarousel
              items={formattedClips}
              renderItem={(clip) => {
                // Type assertion to handle the type check properly
                const typedClip = clip as {
                  id: string | number;
                  title: string;
                  playlistName: string;
                  thumbnailUrl: string;
                  duration: string;
                };
                
                // Ensure clip has all required properties
                if (!typedClip.id || !typedClip.title || !typedClip.playlistName || 
                    !typedClip.thumbnailUrl || !typedClip.duration) {
                  console.error("Invalid clip data for ClipCard:", typedClip);
                  return null;
                }
                
                return <ClipCard clip={typedClip} />;
              }}
            />
          ) : (
            <p className="text-muted-foreground italic">
              {language === 'fr' ? 'Aucun extrait disponible' : 'No clips available'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
